import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { decryptBlobWithPassphrase } from "../lib/crypto";
import { renderMarkdown } from "../lib/markdown";
import { getEnrolledMethod, hasWrapped, methodLabel, rememberPsk, tapUnlock } from "../lib/unlock";
import { useIdleLock } from "../lib/useIdleLock";

const SCOPE = "transcripts";

interface IndexEntry {
  id: string;
  date: string;
  title: string;
  reprompt?: string;
}
interface Meeting {
  date: string;
  title: string;
  transcript: string;
  response: string;
  reprompt?: string;
}

const PSK_KEY = "wn_transcripts_psk";
const SPK_COLORS = ["#5cc8ff", "#7ee787", "#ff7b72", "#d2a8ff", "#ffa657", "#79c0ff"];

const deslug = (title: string) => title.replace(/_/g, " ");

// Speaker-labeled transcript: split on blank lines, parse `spk_N: text` turns.
function SpeakerTranscript({ text }: { text: string }) {
  const turns = useMemo(
    () =>
      text
        .split(/\n\n+/)
        .map((blk) => {
          const m = blk.match(/^spk_(\d+):\s*([\s\S]*)$/);
          return m ? { spk: Number(m[1]), text: m[2].trim() } : { spk: -1, text: blk.trim() };
        })
        .filter((t) => t.text),
    [text],
  );
  return (
    <div className="transcript">
      {turns.map((t, i) => (
        <div key={i} className="turn">
          <span
            className="spk"
            style={{ color: t.spk >= 0 ? SPK_COLORS[t.spk % SPK_COLORS.length] : "var(--muted)" }}
          >
            {t.spk >= 0 ? `spk_${t.spk}` : "—"}
          </span>
          <span className="turn-text">{t.text}</span>
        </div>
      ))}
    </div>
  );
}

export function Transcripts() {
  const [psk, setPsk] = useState(() => sessionStorage.getItem(PSK_KEY) ?? "");
  const [unlocked, setUnlocked] = useState(false);
  const [entries, setEntries] = useState<IndexEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<IndexEntry | null>(null);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [tab, setTab] = useState<"summary" | "transcript">("summary");
  const [showRaw, setShowRaw] = useState(false);
  const [copied, setCopied] = useState(false);
  const cache = useMemo(() => new Map<string, Meeting>(), []);

  async function copyCurrent(m: Meeting) {
    const text = showRaw ? JSON.stringify(m, null, 2) : tab === "summary" ? m.response : m.transcript;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  // Aggressive idle auto-lock — re-unlock is just a tap (or the passphrase).
  const lock = useCallback(() => {
    sessionStorage.removeItem(PSK_KEY);
    setUnlocked(false);
    setSelected(null);
    setMeeting(null);
  }, []);
  useIdleLock(unlocked, lock);
  const [reloading, setReloading] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  async function loadIndex(pass: string, refresh = false): Promise<IndexEntry[]> {
    const enc = await api.transcriptIndex(refresh);
    const json = await decryptBlobWithPassphrase(enc, pass);
    return (JSON.parse(json) as { items: IndexEntry[] }).items ?? [];
  }

  // Re-read the latest meetings live from the source bucket.
  async function refresh() {
    setReloading(true);
    try {
      setEntries(await loadIndex(psk, true));
      setSyncMsg("Refreshed.");
    } catch (e) {
      setSyncMsg("Refresh failed: " + (e as Error).message);
    } finally {
      setReloading(false);
    }
  }

  async function unlock(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      setEntries(await loadIndex(psk));
      setUnlocked(true);
      sessionStorage.setItem(PSK_KEY, psk);
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg.includes("not built") ? msg : "Wrong passphrase (or the index isn't built yet).");
    } finally {
      setBusy(false);
    }
  }

  // Tap the security key: recover the wrapped passphrase and unlock.
  async function tapToUnlock() {
    setError(null);
    setBusy(true);
    try {
      const p = await tapUnlock(SCOPE);
      setEntries(await loadIndex(p));
      setPsk(p);
      setUnlocked(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // Bind the (already-verified) passphrase to the enrolled key. Runs on a fresh
  // click so the WebAuthn tap has valid user activation.
  async function bindKey() {
    setSyncMsg(null);
    try {
      await rememberPsk(SCOPE, psk);
      setSyncMsg("✓ Bound to your key. Lock, then tap to unlock next time.");
    } catch (e) {
      setSyncMsg(null);
      alert("Couldn't bind to key: " + (e as Error).message);
    }
  }

  // Auto-unlock from a passphrase cached this tab session.
  useEffect(() => {
    if (unlocked || !psk) return;
    loadIndex(psk)
      .then((items) => {
        setEntries(items);
        setUnlocked(true);
      })
      .catch(() => sessionStorage.removeItem(PSK_KEY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function open(entry: IndexEntry) {
    setSelected(entry);
    setShowRaw(false);
    setTab("summary");
    setError(null);
    const hit = cache.get(entry.id);
    if (hit) {
      setMeeting(hit);
      return;
    }
    setMeeting(null);
    try {
      const enc = await api.transcriptItem(entry.id);
      const m = JSON.parse(await decryptBlobWithPassphrase(enc, psk)) as Meeting;
      cache.set(entry.id, m);
      setMeeting(m);
    } catch (err) {
      setError("Couldn't open this meeting: " + (err as Error).message);
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => deslug(e.title).toLowerCase().includes(q) || e.date.includes(q));
  }, [entries, query]);

  if (!unlocked) {
    return (
      <div className="center">
        <form className="card" onSubmit={unlock} style={{ width: 440 }}>
          <h2>Transcripts</h2>
          <p className="muted" style={{ marginTop: -8 }}>
            End-to-end encrypted — the server only ever holds ciphertext, and each meeting is
            decrypted in your browser on demand.
          </p>

          {getEnrolledMethod() && hasWrapped(SCOPE) && (
            <>
              <button
                type="button"
                className="primary"
                style={{ width: "100%" }}
                disabled={busy}
                onClick={tapToUnlock}
              >
                {busy ? "…" : `${methodLabel()} — tap to unlock`}
              </button>
              <div className="muted" style={{ textAlign: "center", margin: "10px 0" }}>
                or use the passphrase
              </div>
            </>
          )}

          <div className="field">
            <label>Passphrase</label>
            <input type="password" value={psk} onChange={(e) => setPsk(e.target.value)} autoFocus />
          </div>

          {getEnrolledMethod() && !hasWrapped(SCOPE) && (
            <p className="muted" style={{ fontSize: 11, margin: "0 0 8px" }}>
              First time: unlock with your passphrase, then hit <b>🔑 Bind to key</b> (left panel).
              After that it's tap-only.
            </p>
          )}

          {error && <p className="error">{error}</p>}
          <button className="primary" type="submit" disabled={busy || !psk} style={{ width: "100%" }}>
            {busy ? "…" : "Unlock"}
          </button>

          {!getEnrolledMethod() && (
            <p className="muted" style={{ fontSize: 11, marginBottom: 0 }}>
              Tip: enroll a security key via <b>🔑 Keys</b> (top right) to unlock with a tap.
            </p>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="sidebar">
        <h1>
          Transcripts <small>{entries.length} meetings · decrypted locally</small>
        </h1>
        <div style={{ padding: 8 }}>
          <input
            placeholder="Search title or date…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
        <div style={{ padding: "0 8px 8px", display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={refresh}
              disabled={reloading}
              style={{ flex: 1 }}
              title="Re-read the latest meetings from the source bucket"
            >
              {reloading ? "Refreshing…" : "🔄 Refresh"}
            </button>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {getEnrolledMethod() && !hasWrapped(SCOPE) && (
              <button onClick={bindKey} style={{ flex: 1 }} title="Encrypt this passphrase under your key so you can tap to unlock">
                🔑 Bind to key
              </button>
            )}
            <button className="ghost" onClick={lock} style={{ flex: 1 }}>
              Lock
            </button>
          </div>
          {syncMsg && (
            <p className="muted" style={{ margin: 0, fontSize: 11 }}>
              {syncMsg}
            </p>
          )}
        </div>
        <div className="room-list">
          {filtered.map((e) => (
            <div
              key={e.id}
              className={`room-item ${selected?.id === e.id ? "active" : ""}`}
              onClick={() => open(e)}
            >
              <div>{deslug(e.title)}</div>
              <div className="id">
                {e.date}
                {e.reprompt ? " · re-prompted" : ""}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="muted" style={{ padding: 8 }}>
              No matches.
            </p>
          )}
        </div>
      </div>
      <div className="main">
        {error && selected && (
          <p className="error" style={{ margin: 12 }}>
            {error}
          </p>
        )}
        {!selected ? (
          <div className="center muted">Select a meeting to view.</div>
        ) : !meeting ? (
          <div className="center muted">Decrypting…</div>
        ) : (
          <>
            <div className="room-header">
              <div>
                <div>{deslug(meeting.title)}</div>
                <div className="muted mono" style={{ fontSize: 11 }}>
                  {meeting.date}
                  {meeting.reprompt ? ` · re-prompted from ${meeting.reprompt}` : ""}
                </div>
              </div>
              <div className="spacer" />
              <button
                className={tab === "summary" && !showRaw ? "primary" : "ghost"}
                onClick={() => {
                  setTab("summary");
                  setShowRaw(false);
                }}
              >
                Summary
              </button>
              <button
                className={tab === "transcript" && !showRaw ? "primary" : "ghost"}
                onClick={() => {
                  setTab("transcript");
                  setShowRaw(false);
                }}
              >
                Transcript
              </button>
              <button className={showRaw ? "primary" : "ghost"} onClick={() => setShowRaw((v) => !v)}>
                Raw JSON
              </button>
              <button
                className="ghost"
                onClick={() => copyCurrent(meeting)}
                title={
                  showRaw
                    ? "Copy the raw JSON"
                    : tab === "summary"
                      ? "Copy the whole summary (markdown)"
                      : "Copy the whole transcript"
                }
              >
                {copied ? "✓ Copied" : "📋 Copy all"}
              </button>
            </div>
            <div className="messages" style={{ display: "block" }}>
              {showRaw ? (
                <pre className="body mono" style={{ whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(meeting, null, 2)}
                </pre>
              ) : tab === "summary" ? (
                <div className="body" dangerouslySetInnerHTML={{ __html: renderMarkdown(meeting.response) }} />
              ) : (
                <SpeakerTranscript text={meeting.transcript} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
